// =====================================================
// REFER & EARN — HOW IT WORKS + TERMS & CONDITIONS
//
// The conditions a referred person has to complete, with the action that
// completes each one, followed by the programme's Terms & Conditions. It lives
// on the Refer & Earn page and nowhere else.
//
// It is the SAME for every user and it is PERMANENT:
//   • no personal progress, no ticks, no status chip, no countdown
//   • nothing is ever hidden — a referrer, a referred user and a user with no
//     connection to the programme all see exactly this
//   • it does not disappear when conditions are met or a window closes
//
// It makes no API call of its own. The reward, the qualifying time, the window
// and the list of ACTIVE conditions are passed in from the dashboard the page
// has already loaded — they are the server's live, admin-configured values, so
// a condition an admin adds appears here, and the terms always describe the
// rules the backend actually enforces.
//
// The install step uses the app's existing InstallButton, the same component
// the Login and Home pages use — same install flow, same rule about hiding
// itself once the app is installed. It cannot credit anything; the install is
// recorded from the browser's own signals and the server decides.
// =====================================================

import { Link } from "react-router-dom";
import InstallButton from "../InstallButton";
import "./ReferralHowItWorks.css";

// How each built-in condition type is presented. Admin-added conditions use
// their own name and description.
const STEP_UI = {
  attribution: { icon: "🔗", detail: "Sign up through your friend's referral link or code." },
  install: { icon: "📲", detail: "Add the app to your device and open it.", action: () => <InstallButton inline /> },
  notification: { icon: "🔔", detail: "Allow notifications when asked — and keep them turned on." },
  usage: { icon: "⏱️", detail: "It does not have to be in one go — shorter sessions add up." },
  blog: {
    icon: "✍️",
    detail: "Write and publish one real post — your own words.",
    action: () => (
      <Link className="hw-action" to="/add-blog">
        ✍️ Write Blog
      </Link>
    ),
  },
};

// Shown only if a dashboard without `conditions` is ever rendered.
const FALLBACK = [
  { key: "install", type: "install", name: "Install TheBlogsSphere", required: true },
  { key: "notification", type: "notification", name: "Allow notifications", required: true },
  { key: "blog", type: "blog", name: "Publish one genuine blog", required: true },
  { key: "usage", type: "usage", name: "Use the app for the qualifying time", required: true },
];

export default function ReferralHowItWorks({
  rewardAmount,
  usageMinutes,
  windowHours,
  conditions,
}) {
  const steps = (conditions && conditions.length ? conditions : FALLBACK).map((c) => {
    const ui = STEP_UI[c.type] || {};
    return {
      key: c.key,
      icon: ui.icon || "✅",
      title: c.name,
      detail: STEP_UI[c.type] ? ui.detail : c.description,
      optional: !c.required,
      action: ui.action ? ui.action() : null,
    };
  });
  const required = steps.filter((s) => !s.optional).length;

  return (
    <section className="hw-panel">
      <header className="hw-head">
        <span className="hw-gift" aria-hidden="true">🎁</span>
        <div>
          <h2 className="hw-title">
            Complete {required} steps to unlock ₹{rewardAmount}
          </h2>
          <p className="hw-sub">
            Every required step must be completed within {windowHours}&nbsp;hours of joining
            through a referral link.
          </p>
        </div>
      </header>

      <ol className="hw-steps">
        {steps.map((step, i) => (
          <li key={step.key} className="hw-step">
            <span className="hw-step-icon" aria-hidden="true">{step.icon}</span>
            <div className="hw-step-body">
              <span className="hw-step-title">
                {step.title}
                {step.optional && <em className="hw-optional"> (optional)</em>}
              </span>
              {step.detail && <span className="hw-step-detail">{step.detail}</span>}
              {step.action}
            </div>
            {i < steps.length - 1 && (
              <span className="hw-arrow" aria-hidden="true">↓</span>
            )}
          </li>
        ))}
      </ol>

      <footer className="hw-foot">
        <span className="hw-reward">🎉 ₹{rewardAmount} Referral Reward</span>
        <span className="hw-foot-note">
          Usage is measured on our servers while the app is open and in use —
          background and idle time do not count.
        </span>
      </footer>

      {/* ===== TERMS & CONDITIONS =====
          The programme's single terms section. Every number in it is the
          server's live value, so it cannot promise more than the backend
          enforces. */}
      <details className="hw-terms">
        <summary className="hw-terms-summary">Refer &amp; Earn — Terms &amp; Conditions</summary>
        <div className="hw-terms-body">
          <h3>Mandatory Notification Permission</h3>
          <p>
            Users must allow notifications and keep notification permission enabled to qualify
            for referral rewards. If a referred user denies notification permission, disables
            notifications after granting permission, or otherwise fails to meet the mandatory
            notification requirement, the referral will be marked as ineligible and the reward
            will not be approved or paid unless all applicable eligibility conditions are
            subsequently satisfied under the platform's rules.
          </p>

          <h3>Eligibility</h3>
          <ul>
            <li>
              The reward (currently ₹{rewardAmount} per eligible referral) is subject to all
              active referral eligibility conditions shown above, each of which must be completed
              within {windowHours} hours of joining.
            </li>
            <li>
              Completing the qualifying time ({usageMinutes} minutes of active use) alone does
              not guarantee a reward.
            </li>
            <li>
              Referral rewards are subject to backend verification and anti-fraud checks, and
              every reward is reviewed and approved by our team before it is credited.
            </li>
            <li>
              Duplicate, invalid, or manipulated referral activity is not eligible for rewards.
            </li>
            <li>
              The reward amount, the qualifying time and the conditions may change. A referral is
              paid the amount in effect when it became eligible; rewards already earned or paid
              are never changed.
            </li>
            <li>
             Referral amount will be credited to your wallet within 1 to 7 working days after successful referral verification.
            </li>
          </ul>
        </div>
      </details>
    </section>
  );
}
